<template>
  <div>
    <h2 class="intro-y text-lg font-medium mt-10">Users</h2>
    <p class="mt-3 text-gray-600">
      Last login is Unknown until the user's next login. Dates are shown in
      Central Time. Stale accounts, whether enabled or disabled, are current
      captains of older inactive teams with no history in either of the two
      previous event years. Recent logins and participation on another current
      team exclude an account from review. Inactive Users excludes stale
      accounts.
    </p>
    <ListControls
      v-model:status="status"
      v-model:search="search"
      noun="Users"
      :counts="counts"
      :total="total"
      :review-window="reviewWindow"
      :loaded="loaded"
      :loading="loading"
      :error="error"
      @retry="refresh"
    >
      <router-link :to="{ name: 'new-user' }" class="btn btn-primary"
        >Add New User</router-link
      >
    </ListControls>
    <div v-if="status === 'stale'" class="box p-4 mt-5">
      <p>
        {{ allMatching ? total : selectedIds.length }} account{{
          (allMatching ? total : selectedIds.length) === 1 ? "" : "s"
        }}
        selected{{ allMatching ? " across all matching pages" : "" }}.
      </p>
      <div class="flex flex-wrap gap-3 mt-3">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="
            !total || loading || !!error || !reviewWindow || deactivationBusy
          "
          @click="selectAll"
        >
          Select all {{ total }} matching accounts
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="deactivationBusy"
          @click="clearSelection"
        >
          Clear selection
        </button>
        <button
          type="button"
          class="btn btn-danger"
          :disabled="
            loading || deactivationBusy || (!allMatching && !selectedIds.length)
          "
          @click="reviewSelected"
        >
          Review deactivation
        </button>
      </div>
      <p v-if="allMatching" class="mt-2 text-gray-600">
        All matching accounts are selected. Already inactive accounts will be
        excluded from deactivation. Clear the selection to choose individual
        accounts instead.
      </p>
    </div>
    <UserDeactivation
      :request="deactivationRequest"
      :visible="status === 'stale'"
      @busy-change="deactivationBusy = $event"
      @completed="completed"
    />
    <p v-if="reactivationError" class="mt-4 text-red" role="alert">
      {{ reactivationError }}
    </p>
    <p v-if="reactivationMessage" class="mt-4" role="status">
      {{ reactivationMessage }}
    </p>
    <div class="intro-y mt-5" :aria-busy="loading">
      <UserGrid
        v-if="!loading && !error"
        :grid-data="formattedUsers"
        server-side
        :sort="sort"
        :selectable="status === 'stale' && !!reviewWindow"
        :selected-ids="selectedIds"
        :all-selected="allMatching"
        :actions-disabled="deactivationBusy || !!reactivatingId"
        :allow-reactivation="status !== 'active'"
        :reactivating-id="reactivatingId"
        @sort="changeSort"
        @select="select"
        @deactivate="reviewOne"
        @reactivate="reactivate"
      />
    </div>
    <div
      v-if="totalPages > 1"
      class="mt-5 flex gap-3 justify-center items-center"
    >
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="page <= 1 || loading"
        @click="changePage(page - 1)"
      >
        Previous
      </button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button
        type="button"
        class="btn btn-outline-secondary"
        :disabled="page >= totalPages || loading"
        @click="changePage(page + 1)"
      >
        Next
      </button>
    </div>
  </div>
</template>
<script>
import { computed, defineComponent, ref, watch } from "vue";
import { getUsers, updateUser } from "@/api/users.js";
import UserGrid from "@/components/back-end/UserGrid";
import ListControls from "@/components/back-end/ListControls.vue";
import UserDeactivation from "@/components/back-end/UserDeactivation.vue";
import { useAdminList, errorMessage } from "@/composables/use-admin-list";
import { formatAdminUser } from "@/utils/user-activity";

export default defineComponent({
  components: { UserGrid, ListControls, UserDeactivation },
  setup() {
    const list = useAdminList(getUsers, "createdAt:desc");
    const formattedUsers = computed(() => list.data.value.map(formatAdminUser));
    const selectedIds = ref([]);
    const allMatching = ref(false);
    const deactivationRequest = ref(null);
    const deactivationBusy = ref(false);
    const reactivatingId = ref("");
    const reactivationError = ref("");
    const reactivationMessage = ref("");
    const clearSelection = () => {
      selectedIds.value = [];
      allMatching.value = false;
      deactivationRequest.value = null;
    };
    watch(list.context, () => {
      clearSelection();
      reactivationError.value = "";
      reactivationMessage.value = "";
    });
    const reactivate = async id => {
      if (
        reactivatingId.value ||
        deactivationBusy.value ||
        list.status.value === "active"
      )
        return;
      const user = list.data.value.find(
        user => user.id === id && user.active === false
      );
      if (!user) return;
      reactivatingId.value = id;
      reactivationError.value = "";
      reactivationMessage.value = "";
      try {
        await updateUser({ active: true }, id);
        reactivationMessage.value =
          "Account reactivated. The user can sign in again.";
        await list.refresh();
      } catch (err) {
        reactivationError.value = errorMessage(err);
      } finally {
        reactivatingId.value = "";
      }
    };
    const select = ({ ids, checked }) => {
      if (allMatching.value || deactivationBusy.value) return;
      const selected = new Set(selectedIds.value);
      ids.forEach(id => (checked ? selected.add(id) : selected.delete(id)));
      selectedIds.value = [...selected];
    };
    const selectAll = () => {
      selectedIds.value = [];
      allMatching.value = true;
    };
    const reviewSelected = () => {
      deactivationRequest.value = allMatching.value
        ? { allMatching: true, search: list.appliedSearch.value }
        : { userIds: [...selectedIds.value] };
    };
    const reviewOne = id => {
      deactivationRequest.value = { userIds: [id] };
    };
    const completed = () => {
      clearSelection();
      list.refresh();
    };
    return {
      ...list,
      formattedUsers,
      selectedIds,
      allMatching,
      deactivationRequest,
      deactivationBusy,
      reactivatingId,
      reactivationError,
      reactivationMessage,
      reactivate,
      clearSelection,
      select,
      selectAll,
      reviewSelected,
      reviewOne,
      completed
    };
  }
});
</script>
<style scoped>
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
